import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Wheelchair, X, TextAa, CircleHalf, Article, LinkSimple, Palette, PersonSimpleWalk, ArrowCounterClockwise } from "@phosphor-icons/react";

const DEFAULTS = { text: 0, contrast: false, dyslexia: false, links: false, grayscale: false, motion: false };
const TEXT_LABELS = ["Normal", "Large", "Extra large", "Largest"];

const apply = (s) => {
  const el = document.documentElement;
  el.classList.toggle("a11y-text-1", s.text === 1);
  el.classList.toggle("a11y-text-2", s.text === 2);
  el.classList.toggle("a11y-text-3", s.text === 3);
  el.classList.toggle("a11y-contrast", s.contrast);
  el.classList.toggle("a11y-dyslexia", s.dyslexia);
  el.classList.toggle("a11y-links", s.links);
  el.classList.toggle("a11y-grayscale", s.grayscale);
  el.classList.toggle("a11y-reduce-motion", s.motion);
  localStorage.setItem("smc-a11y", JSON.stringify(s));
};

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(DEFAULTS);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("smc-a11y"));
      if (saved) {
        setState(saved);
        apply(saved);
      }
    } catch {}
  }, []);

  const update = (patch) => {
    const next = { ...state, ...patch };
    setState(next);
    apply(next);
  };

  const Toggle = ({ label, desc, icon: Icon, active, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
        active ? "border-brand bg-brand/[0.06]" : "border-black/10 hover:border-black/25"
      }`}>
      <Icon size={22} weight="duotone" className={active ? "text-brand" : "text-navy"} />
      <span className="flex-1">
        <span className="block text-sm font-semibold text-black">{label}</span>
        <span className="block text-xs text-[#4A4A4A]">{desc}</span>
      </span>
      <span className={`h-5 w-9 rounded-full p-0.5 transition-colors duration-200 ${active ? "bg-brand" : "bg-black/15"}`}>
        <span className={`block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${active ? "translate-x-4" : ""}`} />
      </span>
    </button>
  );

  return (
    <div className="fixed bottom-6 right-6 z-[90]" data-testid="a11y-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-[72px] right-0 w-[320px] rounded-2xl border border-black/10 bg-white p-5 shadow-[0_24px_60px_rgb(0,0,0,0.18)]"
            data-testid="a11y-panel"
            role="dialog"
            aria-label="Accessibility options"
          >
            <div className="mb-4 flex items-center justify-between">
              <p className="font-display text-base font-bold tracking-tight text-navy">Accessibility</p>
              <button onClick={() => setOpen(false)} aria-label="Close accessibility panel" data-testid="a11y-close-btn"
                className="rounded-full p-1.5 text-[#4A4A4A] transition-colors hover:bg-black/5">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="flex flex-col gap-2.5">
              <button onClick={() => update({ text: (state.text + 1) % 4 })} data-testid="a11y-text-size-btn"
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                  state.text > 0 ? "border-brand bg-brand/[0.06]" : "border-black/10 hover:border-black/25"
                }`}>
                <TextAa size={22} weight="duotone" className={state.text > 0 ? "text-brand" : "text-navy"} />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-black">Text size</span>
                  <span className="block text-xs text-[#4A4A4A]">Current: {TEXT_LABELS[state.text]}</span>
                </span>
                <span className="rounded-full bg-navy px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  {state.text === 3 ? "Max" : "A+"}
                </span>
              </button>
              <Toggle label="High contrast" desc="Stronger text and color contrast" icon={CircleHalf}
                active={state.contrast} onClick={() => update({ contrast: !state.contrast })} testid="a11y-contrast-toggle" />
              <Toggle label="Legible font" desc="Switches to Atkinson Hyperlegible" icon={Article}
                active={state.dyslexia} onClick={() => update({ dyslexia: !state.dyslexia })} testid="a11y-dyslexia-toggle" />
              <Toggle label="Highlight links" desc="Underlines and outlines all links" icon={LinkSimple}
                active={state.links} onClick={() => update({ links: !state.links })} testid="a11y-links-toggle" />
              <Toggle label="Grayscale" desc="Removes color from the page" icon={Palette}
                active={state.grayscale} onClick={() => update({ grayscale: !state.grayscale })} testid="a11y-grayscale-toggle" />
              <Toggle label="Reduce motion" desc="Pauses animations and smooth scroll" icon={PersonSimpleWalk}
                active={state.motion} onClick={() => update({ motion: !state.motion })} testid="a11y-motion-toggle" />
              <button onClick={() => update({ ...DEFAULTS })} data-testid="a11y-reset-btn"
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl border border-black/10 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#4A4A4A] transition-colors hover:border-navy hover:text-navy">
                <ArrowCounterClockwise size={15} weight="bold" /> Reset all
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        onClick={() => setOpen(!open)}
        data-testid="a11y-widget-btn"
        aria-label="Open accessibility options"
        aria-expanded={open}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white shadow-[0_12px_30px_rgb(10,37,64,0.35)] transition-colors duration-300 hover:bg-brand"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
      >
        <Wheelchair size={26} weight="duotone" />
      </motion.button>
    </div>
  );
};
